import { useSuperQuery } from '@vadiun/react-hooks-legacy';
import PageContainer from 'layout/components/PageContainer';
import PageHeader from 'layout/components/PageHeader';
import { ArrowLeft, CalendarClock, Image, Receipt } from 'lucide-react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { buttonClasses } from 'shared/components/Button';
import { useClientNames } from 'shared/hooks/useClientNames';
import EventoFilesSection from '../components/EventoDetail/EventoFilesSection';
import EventoGeneralDataCard from '../components/EventoDetail/EventoGeneralDataCard';
import EventoObservationsSection from '../components/EventoDetail/EventoObservationsSection';
import EventoReferentsSection from '../components/EventoDetail/EventoReferentsSection';
import { useEventoDetail } from '../hooks/useEventoDetail';
import useApplicantRoleService from '../services/useApplicantRoleService';
import { EventTransferReservationModel } from '../models/Evento';
import { clearStateAndAddFilterColorState } from 'app/viaje/viajeTraslado/abierto/utils/openedStorage';
import { clearStateAndAddFilterReservationId } from 'app/viaje/viajeTraslado/viajeTraslado/utils/coordinateStorage';
import { clearStateAndAddFilterReservationId as clearStateAndAddFilterReservationIdGestion } from 'app/viaje/viajeTraslado/gestion/utils/gestionStorage';
import EventReserves from '../components/EventoDetail/EventReserves';

export enum ReservationsViews {
  PENDIENTE = 'Pendientes',
  ABIERTO = 'Abiertos',
  CERRADO = 'Cerrados'
}

const EventoDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const eventId = Number(id);
  const history = useHistory();
  const {
    event,
    isLoading,
    updateGeneralData,
    isUpdatingGeneralData,
    addReferent,
    updateReferent,
    removeReferent,
    isAddingReferent,
    isUpdatingReferent,
    addObservation,
    isAddingObservation,
    addSignage,
    updateSignage,
    removeSignage,
    isAddingSignage,
    isUpdatingSignage,
    addBudget,
    updateBudget,
    removeBudget,
    isAddingBudget,
    isUpdatingBudget
  } = useEventoDetail(eventId);

  const { clientesQuery } = useClientNames(false);
  const applicantRoleService = useApplicantRoleService();
  const rolesQuery = useSuperQuery(applicantRoleService.getRoles);

  const handleCreateReserve = () => {
    history.push({
      pathname: '/main/operations/cdr/create-corporate',
      state: {
        eventId,
        clientId: event?.businessClient?.id,
      }
    });
  }

  const handleReservationClick = (
    reservation: EventTransferReservationModel,
    view: ReservationsViews
  ) => {
    if (view === ReservationsViews.ABIERTO) {
      clearStateAndAddFilterColorState({ reservationId: reservation.id });
      history.push('/main/operations/coordination/open-trips');
    } else if (view === ReservationsViews.PENDIENTE) {
      clearStateAndAddFilterReservationId(reservation.id);
      history.push('/main/operations/coordination/pending-trips');
    } else if (view === ReservationsViews.CERRADO) {
      clearStateAndAddFilterReservationIdGestion(reservation.id);
      history.push('/main/operations/cdr/comprehensive-management');
    }
  };

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <PageHeader
        area="Inventarios"
        title={event ? event.name : `Evento #${id}`}
      />
      <PageContainer lessMargin>
        <div className="flex w-full flex-col">
          <Link
            to="/main/inventories/events"
            className={buttonClasses('secondary', 'sm', 'mb-6 self-start')}
          >
            <ArrowLeft size={15} />
            Volver al listado
          </Link>

          {!event && !isLoading && (
            <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-line py-24 text-center dark:border-white/10">
              <CalendarClock
                size={32}
                className="text-secondary-500 dark:text-secondary-300"
              />
              <p className="text-base font-medium text-ink dark:text-ink-dark">
                No se pudo cargar este evento
              </p>
            </div>
          )}

          {event && (
            <div className="flex flex-col gap-6 md:flex-row md:items-start">
              <div className="md:w-96 md:shrink-0">
                <EventoGeneralDataCard
                  event={event}
                  clients={clientesQuery.data ?? []}
                  isSaving={isUpdatingGeneralData}
                  onSave={updateGeneralData}
                />
              </div>

              <div className="flex flex-1 flex-col gap-6">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <EventoReferentsSection
                    referents={event.referents}
                    roles={rolesQuery.data ?? []}
                    isSaving={isAddingReferent || isUpdatingReferent}
                    onAdd={addReferent}
                    onUpdate={updateReferent}
                    onRemove={removeReferent}
                  />
                  <EventoObservationsSection
                    observations={event.observations}
                    isSaving={isAddingObservation}
                    onAdd={addObservation}
                  />
                </div>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <EventoFilesSection
                    title="Cartelería"
                    itemLabel="cartelería"
                    icon={Image}
                    files={event.signages}
                    isSaving={isAddingSignage || isUpdatingSignage}
                    onAdd={addSignage}
                    onUpdate={updateSignage}
                    onRemove={removeSignage}
                  />
                  <EventoFilesSection
                    title="Presupuestos"
                    itemLabel="presupuesto"
                    icon={Receipt}
                    files={event.budgets}
                    isSaving={isAddingBudget || isUpdatingBudget}
                    onAdd={addBudget}
                    onUpdate={updateBudget}
                    onRemove={removeBudget}
                  />
                </div>
                <EventReserves
                  event={event}
                  handleReservationClick={handleReservationClick}
                  handleCreateReserve={handleCreateReserve}
                />
              </div>
            </div>
          )}
        </div>
      </PageContainer>
    </div>
  );
};

export default EventoDetailPage;
